import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { shortenHash } from '@soundxyz/utils/const';
import { styled } from '@components-shared/src/stitches';
import { textStyles } from '@components/Profile/styles';
import { FollowButton } from '@components/basics/FollowButton';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, UserModalInfoFragmentDoc } from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment UserModalInfo on User {
    id
    publicAddress
    webappUri
    username
    avatar {
      id
      url
    }
    artist {
      id
    }
  }
`);

const AVATAR_SIZE = 48;

export const User = ({
  user: userFragment,
  newStyle,
}: {
  user: FragmentType<UserModalInfoFragmentDoc>;
  newStyle?: boolean;
}) => {
  const user = getFragment(UserModalInfoFragmentDoc, userFragment);
  const { id: userId, avatar, artist, publicAddress, webappUri, username } = user;
  const name = user.username;
  const shortenedPublicAddress = shortenHash(publicAddress, 5, 4);

  const profileURL = webappUriPath(webappUri);
  return (
    <UserContainer>
      <AccessibleLink pathnameUri={profileURL}>
        <UserInfoContainer>
          <UserAvatar>
            <Avatar
              size={AVATAR_SIZE}
              src={avatar?.url}
              username={username}
              walletAddress={publicAddress}
              borderRadius={6}
              disableLink
              hasBorder
              webappUri={webappUri}
            />
          </UserAvatar>
          <UserInfo>
            <NameContainer>
              <h2>{name}</h2>
              {artist && <VerifiedTooltip />}
            </NameContainer>
            {/* Add this check so we don't show the publicAddress twice */}
            {name !== shortenedPublicAddress && <h3>{shortenedPublicAddress}</h3>}
          </UserInfo>
        </UserInfoContainer>
      </AccessibleLink>
      <FollowButton
        userId={userId}
        size={newStyle ? 'M' : 'L'}
        variant={newStyle ? 'secondary' : 'primary'}
        sourceLocation="Collected by Modal"
      />
    </UserContainer>
  );
};

const UserContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  alignItems: 'center',
  paddingRight: 16,
  paddingY: 12,

  '@tablet': {
    paddingRight: 24,
    paddingY: 14,
  },
});

const UserInfoContainer = styled('div', {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const UserAvatar = styled('div', {
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  borderRadius: 6,
});

const UserInfo = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '120px',

  h2: {
    ...textStyles.heading,
    fontSize: 16,
    maxWidth: 120,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
  },
  h3: {
    ...textStyles.subHeading,
    lineHeight: '24px',
    fontSize: 14,
  },
});

const NameContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
});
